"use client";

import { useEffect } from "react";

/**
 * Central router for canvas-wide auto-run signals.
 *
 * Two events flow through this bus:
 *   - `auto_generate_node`  → start a single node's `handleGenerate`
 *   - `auto_generate_cancel` → ask a single node to abort an in-flight run
 *
 * Previously every node attached its own window listener, so a canvas with N
 * nodes registered N listeners and event dispatch had to walk through all of
 * them, each filtering on `e.detail.id === id`. With this bus there is
 * exactly ONE window listener per event type and lookups are O(1) by node id.
 *
 * Usage in a node component:
 *
 *   useAutoGenerateHandler(id, useCallback(() => { void handleGenerate(); }, [handleGenerate]));
 *   // optional — only nodes that can be cancelled need this
 *   useAutoCancelHandler(id, useCallback(() => { handleCancel(); }, [handleCancel]));
 */

type Handler = () => void;

const generateHandlers = new Map<string, Handler>();
const cancelHandlers = new Map<string, Handler>();
let generateInstalled = false;
let cancelInstalled = false;

function ensureGenerateInstalled() {
  if (generateInstalled || typeof window === "undefined") return;
  generateInstalled = true;
  window.addEventListener("auto_generate_node", (e: any) => {
    try {
      const id = e?.detail?.id;
      if (!id) return;
      const fn = generateHandlers.get(String(id));
      if (!fn) return;
      try {
        fn();
      } catch {}
    } catch {}
  });
}

function ensureCancelInstalled() {
  if (cancelInstalled || typeof window === "undefined") return;
  cancelInstalled = true;
  window.addEventListener("auto_generate_cancel", (e: any) => {
    try {
      const id = e?.detail?.id as string | undefined;
      // If `id` is omitted, broadcast cancel to every registered node — used
      // by the global "Stop" button to interrupt every in-flight node.
      if (!id) {
        for (const fn of cancelHandlers.values()) {
          try {
            fn();
          } catch {}
        }
        return;
      }
      const fn = cancelHandlers.get(String(id));
      if (!fn) return;
      try {
        fn();
      } catch {}
    } catch {}
  });
}

export function registerAutoGenerateHandler(
  id: string,
  fn: Handler,
): () => void {
  ensureGenerateInstalled();
  generateHandlers.set(String(id), fn);
  return () => {
    if (generateHandlers.get(String(id)) === fn) {
      generateHandlers.delete(String(id));
    }
  };
}

export function registerAutoCancelHandler(
  id: string,
  fn: Handler,
): () => void {
  ensureCancelInstalled();
  cancelHandlers.set(String(id), fn);
  return () => {
    if (cancelHandlers.get(String(id)) === fn) {
      cancelHandlers.delete(String(id));
    }
  };
}

export function useAutoGenerateHandler(id: string, fn: Handler): void {
  useEffect(() => {
    return registerAutoGenerateHandler(id, fn);
  }, [id, fn]);
}

export function useAutoCancelHandler(id: string, fn: Handler): void {
  useEffect(() => {
    return registerAutoCancelHandler(id, fn);
  }, [id, fn]);
}

/** Dispatch helpers — kept here so callers don't have to remember event names. */
export function dispatchAutoGenerate(id: string): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent("auto_generate_node", { detail: { id } }),
    );
  } catch {}
}

export function dispatchAutoCancel(id?: string): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(
        "auto_generate_cancel",
        id ? { detail: { id } } : undefined,
      ),
    );
  } catch {}
}
